import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Calculator, Save, Send, Plus, Trash2, ArrowLeft,
  Package, Wrench, Clock, FileText, Mail, MessageCircle, Printer
} from 'lucide-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { OrdemServico } from '../types'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import SearchableSelect from '../components/ui/SearchableSelect'
import Textarea from '../components/ui/Textarea'
import Modal from '../components/Modal'
import { useToast } from '../components/Toast'
import produtosService from '../services/produtos'
import servicosService from '../services/servicos'
import ordensServicoService from '../services/ordensServico'
import orcamentosService from '../services/orcamentos'
import notificationsService from '../services/notifications'
import apiService from '../services/api'

interface OrcamentoItem {
  id?: number
  tipo: 'PECA' | 'SERVICO'
  descricao: string
  quantidade: number
  valorUnitario: number
  valorTotal: number
}

interface OrcamentoForm {
  ordemServicoId: number
  prazoReparacao: number // dias
  prazoGarantia: number // meses
  observacoes: string
  observacoesInternas?: string
  validadeDias: number
  itens: OrcamentoItem[]
}

// Usar o tipo OrdemServico já definido em types

const OrcamentoFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { orcamentoId } = useParams()
  const { success, error } = useToast()
  const isEditing = !!orcamentoId

  // Estados
  const [formData, setFormData] = useState<OrcamentoForm>({
    ordemServicoId: 0,
    prazoReparacao: 3,
    prazoGarantia: 3,
    observacoes: '',
    observacoesInternas: '',
    validadeDias: 7,
    itens: []
  })
  const [selectedOS, setSelectedOS] = useState<OrdemServico | null>(null)
  const [currentOrcamentoId, setCurrentOrcamentoId] = useState<number | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)
  const [sendOptions, setSendOptions] = useState({
    email: false,
    whatsapp: false,
    print: false
  })
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [isGeneratingCupom, setIsGeneratingCupom] = useState(false)

  // Estados para pesquisa de peças e serviços
  const [pecasOptions, setPecasOptions] = useState<{ value: string; label: string; data?: any }[]>([])
  const [servicosOptions, setServicosOptions] = useState<{ value: string; label: string; data?: any }[]>([])
  const [isLoadingPecas, setIsLoadingPecas] = useState(false)
  const [isLoadingServicos, setIsLoadingServicos] = useState(false)

  // Buscar ordens de serviço do backend (apenas OSs ativas, não canceladas/concluídas)
  const { data: ordensServicoResponse, isLoading: isLoadingOS } = useQuery({
    queryKey: ['ordens-servico-para-orcamento'],
    queryFn: () => ordensServicoService.list({ limit: 100 }), // Buscar todas as OS disponíveis
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  })

  // Query para carregar orçamento existente (quando editando)
  const { data: orcamentoExistente, isLoading: isLoadingOrcamento } = useQuery({
    queryKey: ['orcamento', orcamentoId],
    queryFn: () => orcamentosService.getById(parseInt(orcamentoId!)),
    enabled: isEditing && !!orcamentoId,
    staleTime: 0 // Sempre buscar dados atualizados
  })

  // Filtrar apenas OSs que podem receber orçamento (não canceladas, não concluídas, não entregues, não aguardando aprovação)
  const ordensDisponiveis = ordensServicoResponse?.ordensServico?.filter(os => {
    const estadoNome = os.estado?.nome || ''
    const isEstadoFinal = [
      'CANCELADO', 'CONCLUÍDO', 'ENTREGUE', 'CANCELADA', 'CONCLUÍDA', 'ENTREGUE',
      'AGUARDA APROVAÇÃO', 'AGUARDANDO APROVAÇÃO'
    ].includes(estadoNome.toUpperCase())

    return !isEstadoFinal
  }) || []

  // Mock data para peças e serviços
  const mockPecas = [
    { id: 1, nome: 'Tela LCD iPhone 13 Pro', valor: 150.00 },
    { id: 2, nome: 'Bateria iPhone 13 Pro', valor: 89.90 },
    { id: 3, nome: 'Bateria Samsung Galaxy S21', valor: 65.00 },
    { id: 4, nome: 'Tela Samsung Galaxy S21', valor: 125.00 }
  ]

  const mockServicos = [
    { id: 1, nome: 'Troca de tela', valor: 35.50 },
    { id: 2, nome: 'Substituição de bateria', valor: 24.90 },
    { id: 3, nome: 'Limpeza e manutenção', valor: 15.00 },
    { id: 4, nome: 'Diagnóstico completo', valor: 20.00 }
  ]

  useEffect(() => {
    if (formData.ordemServicoId && ordensDisponiveis.length > 0) {
      const os = ordensDisponiveis.find(os => os.id === formData.ordemServicoId)
      setSelectedOS(os || null)
    }
  }, [formData.ordemServicoId, ordensDisponiveis])

  // Carregar dados do orçamento existente quando editando
  useEffect(() => {
    if (orcamentoExistente && isEditing) {
      console.log('Carregando orçamento existente:', orcamentoExistente)
      setCurrentOrcamentoId(orcamentoExistente.id)

      setFormData({
        ordemServicoId: orcamentoExistente.ordemServicoId,
        prazoReparacao: parseInt(orcamentoExistente.prazoReparacao?.replace(' dias', '') || '3'),
        prazoGarantia: parseInt(orcamentoExistente.garantia?.replace(' meses', '') || '3'),
        observacoes: orcamentoExistente.observacoesCliente || '',
        observacoesInternas: orcamentoExistente.observacoesInternas || '',
        validadeDias: orcamentoExistente.validadeAte ?
          Math.ceil((new Date(orcamentoExistente.validadeAte).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 7,
        itens: orcamentoExistente.itens?.map(item => ({
          id: item.id,
          tipo: item.tipo as 'PECA' | 'SERVICO',
          descricao: item.descricao,
          quantidade: item.quantidade,
          valorUnitario: item.valorUnitario,
          valorTotal: item.quantidade * item.valorUnitario
        })) || []
      })
    }
  }, [orcamentoExistente, isEditing])

  const calcularValorTotal = () => {
    return formData.itens.reduce((total, item) => total + item.valorTotal, 0)
  }

  const adicionarItem = (tipo: 'PECA' | 'SERVICO') => {
    const novoItem: OrcamentoItem = {
      tipo,
      descricao: '',
      quantidade: 1,
      valorUnitario: 0,
      valorTotal: 0
    }
    setFormData({
      ...formData,
      itens: [...formData.itens, novoItem]
    })
  }

  const atualizarItem = (index: number, field: keyof OrcamentoItem, value: any) => {
    const novosItens = [...formData.itens]
    novosItens[index] = { ...novosItens[index], [field]: value }

    // Recalcular valor total do item
    if (field === 'quantidade' || field === 'valorUnitario') {
      novosItens[index].valorTotal = novosItens[index].quantidade * novosItens[index].valorUnitario
    }

    setFormData({ ...formData, itens: novosItens })
  }

  const removerItem = (index: number) => {
    const novosItens = formData.itens.filter((_, i) => i !== index)
    setFormData({ ...formData, itens: novosItens })
  }

  const selecionarPecaOuServico = (index: number, tipo: 'PECA' | 'SERVICO', itemId: string) => {
    const lista = tipo === 'PECA' ? mockPecas : mockServicos
    const item = lista.find(p => p.id.toString() === itemId)

    if (item) {
      atualizarItem(index, 'descricao', item.nome)
      atualizarItem(index, 'valorUnitario', item.valor)
    }
  }

  const handleSave = async (status: 'RASCUNHO' | 'ENVIADO' = 'RASCUNHO') => {
    if (!formData.ordemServicoId) {
      error('Selecione uma ordem de serviço')
      return
    }

    if (formData.itens.length === 0) {
      error('Adicione pelo menos um item ao orçamento')
      return
    }

    try {
      // Criar data de validade
      const hoje = new Date()
      const validadeAte = new Date(hoje.getTime() + (formData.validadeDias * 24 * 60 * 60 * 1000))

      // Preparar dados para o backend
      const orcamentoData = {
        ordemServicoId: formData.ordemServicoId,
        prazoReparacao: `${formData.prazoReparacao} dias`,
        garantia: `${formData.prazoGarantia} meses`,
        validadeAte: validadeAte.toISOString(),
        observacoesCliente: formData.observacoes,
        observacoesInternas: formData.observacoesInternas,
        itens: formData.itens.map(item => ({
          tipo: item.tipo,
          descricao: item.descricao,
          quantidade: item.quantidade,
          valorUnitario: item.valorUnitario
        }))
      }

      let orcamentoId = currentOrcamentoId

      let isNewOrcamento = false

      // Se não existe orçamento, criar novo
      if (!orcamentoId) {
        const orcamento = await orcamentosService.create(orcamentoData)
        orcamentoId = orcamento.id
        setCurrentOrcamentoId(orcamentoId)
        isNewOrcamento = true
      } else {
        // Se existe orçamento, atualizar os dados
        await orcamentosService.update(orcamentoId, orcamentoData)
      }

      if (status === 'ENVIADO') {
        // Atualizar status para enviado (isso acionará a mudança de estado da OS)
        await orcamentosService.send(orcamentoId)
        success('Orçamento atualizado e enviado com sucesso! O estado da ordem de serviço foi atualizado.')
      } else {
        success(isEditing ? 'Orçamento atualizado com sucesso!' : 'Orçamento salvo como rascunho!')

        // Se é um orçamento novo (não é edição), mostrar modal de envio
        if (isNewOrcamento) {
          await generateCupomPreview()
          setShowSendModal(true)
          return // Não navegar ainda, esperar o usuário confirmar ou fechar o modal
        }
      }

      // Salvar PDF automaticamente em background
      try {
        // Buscar orçamento completo para gerar token
        const orcamentoCompleto = await orcamentosService.findById(orcamentoId)
        if (orcamentoCompleto) {
          const token = generatePublicToken(orcamentoCompleto.id, orcamentoCompleto.criadoEm)
          // Gerar PDF em background - não aguardar para não atrasar o usuário
          fetch(`/api/public/orcamento/${orcamentoCompleto.id}/${token}/pdf`, {
            method: 'GET'
          }).catch(err => console.warn('PDF em background falhou:', err))
        }
      } catch (err) {
        console.warn('Erro ao salvar PDF automaticamente:', err)
        // Não mostrar erro para o usuário, é apenas um backup
      }

      navigate('/orcamentos')
    } catch (err: any) {
      console.error('Erro ao salvar orçamento:', err)
      error(`Erro ao salvar orçamento: ${err.response?.data?.message || err.message}`)
    }
  }

  const handleSend = async () => {
    await generateCupomPreview()
    setShowSendModal(true)
  }

  const generateCupomPreview = async () => {
    setIsGeneratingCupom(true)
    try {
      // Primeiro salvar o orçamento se ainda não foi salvo
      if (!formData.ordemServicoId) {
        error('Selecione uma ordem de serviço')
        return
      }

      if (formData.itens.length === 0) {
        error('Adicione pelo menos um item ao orçamento')
        return
      }

      let orcamentoId = currentOrcamentoId

      // Se ainda não existe orçamento, criar um novo
      if (!orcamentoId) {
        // Criar data de validade
        const hoje = new Date()
        const validadeAte = new Date(hoje.getTime() + (formData.validadeDias * 24 * 60 * 60 * 1000))

        // Preparar dados para o backend
        const orcamentoData = {
          ordemServicoId: formData.ordemServicoId,
          prazoReparacao: `${formData.prazoReparacao} dias`,
          garantia: `${formData.prazoGarantia} meses`,
          validadeAte: validadeAte.toISOString(),
          observacoesCliente: formData.observacoes,
          observacoesInternas: formData.observacoesInternas,
          itens: formData.itens.map(item => ({
            tipo: item.tipo,
            descricao: item.descricao,
            quantidade: item.quantidade,
            valorUnitario: item.valorUnitario
          }))
        }

        // Criar orçamento como rascunho
        const orcamento = await orcamentosService.create(orcamentoData)
        orcamentoId = orcamento.id
        setCurrentOrcamentoId(orcamentoId)
      }

      // Gerar URL do PDF com token de autenticação
      const token = localStorage.getItem('token')
      const url = `/api/orcamentos/${orcamentoId}/cupom/orcamento?token=${token}`
      setPdfUrl(url)

    } catch (err: any) {
      console.error('Erro ao gerar preview:', err)
      error(`Erro ao gerar orçamento: ${err.response?.data?.message || err.message}`)
    } finally {
      setIsGeneratingCupom(false)
    }
  }

  const generateAndPrintCupom = async () => {
    if (!currentOrcamentoId || !pdfUrl) return

    try {
      // Abrir PDF em nova janela para impressão
      const printWindow = window.open(pdfUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
        success('Cupom PDF aberto para impressão')
      } else {
        error('Bloqueador de pop-up impediu a abertura do PDF')
      }
    } catch (err) {
      console.error('Erro ao imprimir cupom:', err)
      error('Erro ao imprimir cupom PDF')
    }
  }

  // Gerar token público para o orçamento
  const generatePublicToken = (orcamentoId: number, criadoEm: string): string => {
    return btoa(`${orcamentoId}-${criadoEm}`).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16)
  }

  // Gerar URL pública para o orçamento
  const generatePublicURL = (orcamentoId: number, token: string): string => {
    const baseURL = window.location.origin.replace(':4051', ':4050') // Trocar porta do frontend pela do backend
    return `${baseURL}/api/public/orcamento/${orcamentoId}/${token}`
  }

  const confirmarEnvio = async () => {
    try {
      console.log('🚀 confirmarEnvio - Iniciando com opções:', sendOptions)

      // Se pelo menos uma das opções foi selecionada, mudar estado para ENVIADO/Aguarda Aprovação
      if (sendOptions.email || sendOptions.whatsapp || sendOptions.print) {
        setShowSendModal(false)

        console.log('📝 Orçamento ID:', currentOrcamentoId)

        // Primeiro atualizar status para ENVIADO se necessário
        if (currentOrcamentoId) {
          console.log('📤 Atualizando status para ENVIADO...')
          await orcamentosService.send(currentOrcamentoId)
          console.log('✅ Status atualizado')
        }

        // Gerar link público para o orçamento
        if (currentOrcamentoId && (sendOptions.email || sendOptions.whatsapp)) {
          console.log('🔗 Gerando link público...')
          // Buscar o orçamento recém-criado/atualizado para obter a data de criação
          const orcamento = await orcamentosService.getById(currentOrcamentoId)
          console.log('✅ Orçamento obtido:', orcamento)

          const token = generatePublicToken(currentOrcamentoId, orcamento.criadoEm)
          const publicURL = generatePublicURL(currentOrcamentoId, token)
          console.log('🔗 URL pública gerada:', publicURL)

          if (sendOptions.whatsapp) {
            console.log('📱 Enviando WhatsApp...')
            await sendWhatsAppMessage(orcamento, publicURL)
          }

          if (sendOptions.email) {
            console.log('📧 Enviando Email...')
            await sendEmailMessage(orcamento, publicURL)
          }
        }

        if (sendOptions.print) {
          console.log('🖨️ Imprimindo cupom...')
          // Imprimir cupom PDF
          if (pdfUrl) {
            await generateAndPrintCupom()
          }
        }

        // Navegar de volta para a lista de orçamentos
        console.log('✅ Processo concluído, navegando para /orcamentos')
        navigate('/orcamentos')

      } else {
        error('Selecione pelo menos uma opção: Email, WhatsApp ou Imprimir')
      }

    } catch (err: any) {
      console.error('❌ Erro em confirmarEnvio:', err)
      error(`Erro ao processar orçamento: ${err.message}`)
    }
  }

  const fecharModalSemEnviar = () => {
    setShowSendModal(false)
    navigate('/orcamentos')
  }

  // Enviar mensagem via WhatsApp
  const sendWhatsAppMessage = async (orcamento: any, publicURL: string) => {
    try {
      console.log('📱 sendWhatsAppMessage - Iniciando:', { orcamentoId: orcamento.id, publicURL })

      // Verificar se cliente tem telefone cadastrado
      if (!selectedOS?.cliente.telefonePrincipal) {
        console.error('❌ Cliente não possui telefone cadastrado')
        error('Cliente não possui telefone cadastrado')
        return
      }

      console.log('📤 Enviando notificação via API...')
      // Enviar notificação usando o sistema integrado
      const result = await notificationsService.sendOrcamentoNotification(orcamento.id, 'CRIACAO')
      console.log('✅ Resposta da API:', result)

      success('Notificação enviada por WhatsApp com sucesso')
    } catch (err: any) {
      console.error('❌ Erro ao enviar WhatsApp:', err)
      console.error('❌ Erro completo:', JSON.stringify(err, null, 2))
      error(err.response?.data?.message || err.message || 'Erro ao enviar por WhatsApp')
    }
  }

  // Enviar email
  const sendEmailMessage = async (orcamento: any, publicURL: string) => {
    try {
      // Verificar se cliente tem email cadastrado
      if (!selectedOS?.cliente.email) {
        error('Cliente não possui email cadastrado')
        return
      }

      // Enviar notificação usando o sistema integrado
      await notificationsService.sendOrcamentoNotification(orcamento.id, 'CRIACAO')

      success('Notificação enviada por email com sucesso')
    } catch (err: any) {
      console.error('Erro ao enviar email:', err)
      error(err.response?.data?.message || 'Erro ao enviar por email')
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value)
  }

  // Funções de pesquisa para peças e serviços
  const searchPecas = useCallback(async (search: string) => {
    setIsLoadingPecas(true)
    try {
      const pecas = await produtosService.searchPecas(search)
      const options = pecas.map(peca => ({
        value: peca.id.toString(),
        label: `${peca.nome} - ${formatCurrency(peca.precoVenda)}`,
        data: peca
      }))
      setPecasOptions(options)
    } catch (error) {
      console.error('Erro ao buscar peças:', error)
      setPecasOptions([])
    } finally {
      setIsLoadingPecas(false)
    }
  }, [])

  const searchServicos = useCallback(async (search: string) => {
    setIsLoadingServicos(true)
    try {
      const servicos = await servicosService.searchServicos(search)
      const options = servicos.map(servico => ({
        value: servico.id.toString(),
        label: `${servico.nome} - ${formatCurrency(servico.preco)}`,
        data: servico
      }))
      setServicosOptions(options)
    } catch (error) {
      console.error('Erro ao buscar serviços:', error)
      setServicosOptions([])
    } finally {
      setIsLoadingServicos(false)
    }
  }, [])

  const selecionarPecaOuServicoNovo = (index: number, value: string, option?: any) => {
    if (!option?.data) return

    const novosItens = [...formData.itens]
    const item = option.data

    novosItens[index] = {
      ...novosItens[index],
      descricao: item.nome,
      valorUnitario: item.precoVenda || item.preco,
      valorTotal: novosItens[index].quantidade * (item.precoVenda || item.preco)
    }

    setFormData({ ...formData, itens: novosItens })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/orcamentos')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Voltar
            </Button>
            <Calculator className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editar Orçamento' : 'Novo Orçamento'}
              </h1>
              <p className="text-gray-600">
                {selectedOS ? `${selectedOS.numero} - ${selectedOS.cliente.nome}` : 'Selecione uma ordem de serviço'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleSave('RASCUNHO')}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Salvar Rascunho
            </Button>
            <Button
              onClick={handleSend}
              leftIcon={<Send className="h-4 w-4" />}
              disabled={!selectedOS || formData.itens.length === 0}
            >
              Enviar para Cliente
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 space-y-6">
        {/* Informações Básicas */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-medium mb-4">Informações Básicas</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Ordem de Serviço *"
              value={formData.ordemServicoId.toString()}
              onChange={(e) => setFormData({ ...formData, ordemServicoId: parseInt(e.target.value) })}
              disabled={isLoadingOS}
              options={[
                { value: '0', label: isLoadingOS ? 'Carregando...' : 'Selecione uma OS' },
                ...ordensDisponiveis.map(os => ({
                  value: os.id.toString(),
                  label: `${os.numero} - ${os.cliente.nome} (${os.equipamento.modelo.marca.nome} ${os.equipamento.modelo.nome})`
                }))
              ]}
            />

            <Input
              label="Validade do Orçamento (dias)"
              type="number"
              value={formData.validadeDias}
              onChange={(e) => setFormData({ ...formData, validadeDias: parseInt(e.target.value) || 7 })}
              min={1}
              max={30}
            />

            <Input
              label="Prazo de Reparação (dias)"
              type="number"
              value={formData.prazoReparacao}
              onChange={(e) => setFormData({ ...formData, prazoReparacao: parseInt(e.target.value) || 1 })}
              min={1}
              max={30}
            />

            <Input
              label="Prazo de Garantia (meses)"
              type="number"
              value={formData.prazoGarantia}
              onChange={(e) => setFormData({ ...formData, prazoGarantia: parseInt(e.target.value) || 1 })}
              min={1}
              max={24}
            />
          </div>

          {selectedOS && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Detalhes da OS</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Cliente:</span>
                  <span className="ml-2 font-medium">{selectedOS.cliente.nome}</span>
                </div>
                <div>
                  <span className="text-gray-600">Equipamento:</span>
                  <span className="ml-2 font-medium">{selectedOS.equipamento.modelo.marca.nome} {selectedOS.equipamento.modelo.nome}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Problema:</span>
                  <span className="ml-2 font-medium">{selectedOS.problemaRelatado}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Itens do Orçamento */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Itens do Orçamento</h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => adicionarItem('PECA')}
                leftIcon={<Package className="h-4 w-4" />}
              >
                Adicionar Peça
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => adicionarItem('SERVICO')}
                leftIcon={<Wrench className="h-4 w-4" />}
              >
                Adicionar Serviço
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {formData.itens.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {item.tipo === 'PECA' ? (
                      <Package className="h-5 w-5 text-blue-500" />
                    ) : (
                      <Wrench className="h-5 w-5 text-green-500" />
                    )}
                    <span className="font-medium">
                      {item.tipo === 'PECA' ? 'Peça' : 'Serviço'} #{index + 1}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removerItem(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-2">
                    <SearchableSelect
                      label={`Pesquisar ${item.tipo === 'PECA' ? 'Peça' : 'Serviço'}`}
                      placeholder={`Digite para pesquisar ${item.tipo === 'PECA' ? 'peças' : 'serviços'}...`}
                      value=""
                      options={item.tipo === 'PECA' ? pecasOptions : servicosOptions}
                      onChange={(value, option) => selecionarPecaOuServicoNovo(index, value, option)}
                      onSearch={item.tipo === 'PECA' ? searchPecas : searchServicos}
                      isLoading={item.tipo === 'PECA' ? isLoadingPecas : isLoadingServicos}
                    />
                    <Input
                      label="Descrição *"
                      value={item.descricao}
                      onChange={(e) => atualizarItem(index, 'descricao', e.target.value)}
                      placeholder="Descrição do item"
                      className="mt-2"
                    />
                  </div>

                  <Input
                    label="Quantidade *"
                    type="number"
                    value={item.quantidade}
                    onChange={(e) => atualizarItem(index, 'quantidade', parseInt(e.target.value) || 0)}
                    min={1}
                  />

                  <Input
                    label="Valor Unitário *"
                    type="number"
                    step="0.01"
                    value={item.valorUnitario}
                    onChange={(e) => atualizarItem(index, 'valorUnitario', parseFloat(e.target.value) || 0)}
                    min={0}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Total
                    </label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(item.valorTotal)}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {formData.itens.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum item adicionado ao orçamento</p>
                <p className="text-sm">Clique em "Adicionar Peça" ou "Adicionar Serviço" para começar</p>
              </div>
            )}
          </div>

          {formData.itens.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex justify-end">
                <div className="text-right">
                  <p className="text-sm text-gray-600">Valor Total do Orçamento</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calcularValorTotal())}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Observações */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-medium mb-4">Observações</h2>

          <div className="space-y-4">
            <Textarea
              label="Observações para o Cliente"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Informações importantes que o cliente deve saber..."
              rows={4}
            />

            <Textarea
              label="Observações Internas"
              value={formData.observacoesInternas || ''}
              onChange={(e) => setFormData({ ...formData, observacoesInternas: e.target.value })}
              placeholder="Notas internas para a equipe..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Modal de Envio */}
      <Modal
        isOpen={showSendModal}
        onClose={() => {
          setShowSendModal(false)
          setPdfUrl('')
          setSendOptions({ email: false, whatsapp: false, print: false })
        }}
        title="Orçamento - Visualizar e Enviar"
        size="xl"
      >
        <div className="space-y-6">
          {/* Cliente Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Cliente: {selectedOS?.cliente.nome}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>📧 {selectedOS?.cliente.email || 'Email não cadastrado'}</div>
              <div>📱 {selectedOS?.cliente.telefonePrincipal || 'Telefone não cadastrado'}</div>
            </div>
          </div>

          {/* Preview do PDF */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 flex items-center justify-between">
              <h4 className="font-medium text-gray-700">Preview do Cupom PDF</h4>
              <div className="text-sm text-gray-600">
                Valor Total: {formatCurrency(calcularValorTotal())}
              </div>
            </div>
            <div className="bg-gray-50" style={{ height: '600px' }}>
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title="Preview Cupom PDF Orçamento"
                />
              ) : isGeneratingCupom ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Gerando preview do cupom PDF...</p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Preview não disponível</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Opções de Envio/Impressão */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Como deseja proceder com o orçamento?</h4>

            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={sendOptions.email}
                onChange={(e) => setSendOptions({ ...sendOptions, email: e.target.checked })}
                className="rounded border-gray-300"
                disabled={!selectedOS?.cliente.email}
              />
              <Mail className="h-5 w-5 text-blue-500" />
              <div className="flex-1">
                <div className="font-medium">Enviar por Email</div>
                <div className="text-sm text-gray-500">
                  {selectedOS?.cliente.email || 'Email não cadastrado'}
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={sendOptions.whatsapp}
                onChange={(e) => setSendOptions({ ...sendOptions, whatsapp: e.target.checked })}
                className="rounded border-gray-300"
                disabled={!selectedOS?.cliente.telefonePrincipal}
              />
              <MessageCircle className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <div className="font-medium">Enviar por WhatsApp</div>
                <div className="text-sm text-gray-500">
                  {selectedOS?.cliente.telefonePrincipal || 'Telefone não cadastrado'}
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={sendOptions.print}
                onChange={(e) => setSendOptions({ ...sendOptions, print: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Printer className="h-5 w-5 text-gray-700" />
              <div className="flex-1">
                <div className="font-medium">Imprimir</div>
                <div className="text-sm text-gray-500">
                  Abre o PDF em nova aba para impressão
                </div>
              </div>
            </label>
          </div>

          {/* Aviso importante */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Importante:</p>
                <p>Qualquer uma das opções acima (Email, WhatsApp ou Impressão) irá automaticamente alterar o estado da ordem de serviço para <strong>"Aguarda Aprovação"</strong>.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={fecharModalSemEnviar}
            >
              Não Enviar Agora
            </Button>
            <Button
              onClick={confirmarEnvio}
              leftIcon={<Send className="h-4 w-4" />}
              disabled={!sendOptions.email && !sendOptions.whatsapp && !sendOptions.print}
            >
              Executar Ações Selecionadas
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default OrcamentoFormPage